<template>
  <!-- No h-dvh here: the .pageWrapper rule sets the height from the VISIBLE
       viewport (svh). Tailwind's h-dvh utility would win the cascade and lay
       the page out to the large viewport, pushing the footer behind Safari's
       bottom bar. -->
  <div class="studio-layered-mobile pageWrapper flex flex-col overflow-hidden">
    <!-- Modern Top Floating Controls (Figma 4783:134648..4783:134657) -->
    <ToolsMobileToolsHeaderControls @view-in-room="$emit('viewInRoom')" />

    <div id="studioContainer" class="flex flex-col flex-1 overflow-hidden">
      <!-- z-0 keeps this below the sheet's z-[1] wrapper. mainContent is
           position:relative, so it would otherwise paint over the arrow row,
           which overlaps its bottom edge by 18px.

           overflow-visible so the canvas can paint past mainContent's bottom
           edge and run under the sheet, where the sheet's rounded corner
           cutout reveals it (CanvasGrid's extend-behind-sheet prop).

           min-h-0 is REQUIRED alongside it. As a flex item, mainContent
           defaults to min-height:auto, which makes its minimum size
           content-based - with the clip gone, the (taller) canvas child
           inflated mainContent to 3000px and pushed the sheet off-screen.
           min-h-0 pins the height to the flex layout instead.

           The overflow is bounded above by #studioContainer (overflow:hidden)
           and .pageWrapper, both of which clip at the viewport, so the
           overflowed band can never escape the shell. -->
      <div
        id="mainContent"
        class="flex-1 min-h-0 overflow-visible relative z-0"
        @click="onCanvasBackgroundClick"
      >
        <ClientOnly>
          <CanvasGrid v-if="gridStore.gridPattern" extend-behind-sheet />
        </ClientOnly>
      </div>
      <ClientOnly>
        <!-- The bottom section's surface.

             Below the corner band the wrapper is solid white, which is the
             sheet's own surface and runs to the bottom of the page. Its TOP
             band is transparent so the sheet's rounded corners - which are a
             cutout, not a border - reveal the canvas behind them: the grid
             the user is editing, drawn past the sheet's top edge by
             CanvasGrid's extend-behind-sheet prop.

             The wrapper's own box is only as tall as the sheet (288px), so it
             stops flush with the bottom of the footer. On iOS Safari the
             page is laid out taller than the visible area and the browser
             floats its bottom bar over the overflow, so that overflow region
             needs to be the sheet's white too - otherwise #mainContent's
             canvas grey shows through the bar as a grey band under "Add to
             Cart".

             The white that covers it is a ::after BEHIND the sheet
             (z-index 0) rather than a background on the wrapper: a
             background would also cover the sheet's rounded top corners.

             When the sheet is COLLAPSED the wrapper must not paint at all,
             or the white would cover the canvas at the bottom of the
             screen. The sheet mirrors its state here as sheetCollapsed. -->
        <div
          class="mobile-sheet-surface relative z-[1] flex"
          :class="sheetCollapsed ? 'mobile-sheet-surface--collapsed' : 'mobile-sheet-surface--open'"
        >
          <!-- Nothing paints behind the sheet's rounded top corners.

               The corner radius on the card is a CUTOUT, not a border: the
               radius leaves the corner transparent, and whatever is BEHIND
               the sheet shows through it. That is what makes the rounding
               visible - paint the cutout and the corners read as square.

               This used to be a flat canvas-grey strip standing in for the
               canvas. But the canvas is drawn by Konva and is CLIPPED to
               #mainContent, whose bottom edge is the sheet's top edge - so
               there was no grid behind the cutout to reveal, and the grey
               read as a flat wedge instead of the grid the user is editing.

               The canvas now draws past that edge (CanvasGrid's
               extend-behind-sheet prop), so the real grid runs under the
               sheet and shows through the cutout. This element is kept as
               the band that sits behind the card, transparent, so the
               cutout resolves to the canvas rather than to the page
               background. It is positioned rather than sized into the
               flow, so it cannot affect the layout or the collapse
               animation. -->
          <div
            class="mobile-sheet-corner-strip absolute left-0 right-0 top-0 h-[calc(var(--sheet-corner-radius)+4px)] bg-transparent pointer-events-none z-0"
            :class="sheetCollapsed ? 'hidden' : ''"
          />
          <ToolsMobileTools />
        </div>
      </ClientOnly>
      <ClientOnly>
        <!-- Modern Color Flyout Drawer (Figma 7540:150291, 7154:232015 for layered).

             A SIBLING of the sheet surface rather than a child of #mainContent.
             Inside #mainContent it was trapped in that element's stacking
             context (z-index: 0), so the sheet (z-index: 1) painted over it,
             and #mainContent's height - the canvas height, i.e. the viewport
             minus the sheet - clipped the panel on short screens. Here it
             overlays the sheet instead: the drawer is fixed to the page, so
             it is never clipped by the shell's overflow, and its own z-index
             (20) lifts it above the sheet (1) and the header (30 keeps the
             header's buttons clickable above the drawer). -->
        <ToolsMobileToolsColorDrawer />
      </ClientOnly>
    </div>
    <ClientOnly>
      <Modal />
      <Tutorial />
      <Loading v-if="loadingStore.isLoading" />
      <ARViewerModal
        :isOpen="arModalOpen"
        :modelUrl="arModelUrl"
        :arPageUrl="arPageUrl"
        :loading="isExportingAR"
        :error="arError"
        :autoLaunchAR="arAutoLaunch"
        :iosSrc="null"
        @close="closeARModal"
        @retry="handleARRetry"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { SELECTABLE_TOOLS } from "~/composables/grid/constants";
import { useUserStore } from "~/stores/user";

const gridStore = useGridStore();
const loadingStore = useLoadingStore();
const userStore = useUserStore();

// The bottom sheet's own state, mirrored from MobileTools/index.vue so the
// wrapper can stop painting white when the sheet is collapsed (the canvas
// should show through to the bottom of the screen).
const sheetCollapsed = useState<boolean>('mobile-sheet-collapsed', () => false)

onMounted(() => {
  userStore.initializeFromCookie();
});

defineEmits(["viewInRoom"]);

defineProps({
  arModalOpen: Boolean,
  arModelUrl: String,
  arPageUrl: String,
  isExportingAR: Boolean,
  arError: String,
  arAutoLaunch: Boolean,
  closeARModal: Function,
  handleARRetry: Function,
});

// Per FR-62 mobile UX: tapping outside the active grid resets the current
// tool back to the default pointer/select mode and clears any selected
// items. We only treat clicks whose target is the mainContent background
// itself (not the canvas or any other interactive child) as an "outside" tap.
const onCanvasBackgroundClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null
  if (!target || target.id !== 'mainContent') return
  if (gridStore.selectedItemsOnGrid?.length) {
    gridStore.unselectItemsOnGrid()
  }
  if (gridStore.selectedTool !== SELECTABLE_TOOLS.SELECT) {
    gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
  }
};
</script>

<style scoped>
@import "./NewStudio/tokens.css";
.pageWrapper {
  /* The shell fills the VISIBLE viewport.

     `h-dvh` / `100dvh` resolve to the LARGE viewport on iOS Safari (the height
     the window would have with the browser bar collapsed), so with the bar
     expanded the page laid out ~100px taller than the area on screen. The
     footer - the bottom of the sheet - was then pushed behind the bar, and
     the canvas grey showed through the strip below it, reading as a grey band
     under "Add to Cart".

     `100svh` is the small viewport: the height with the browser bars shown,
     which is what is actually visible, so the sheet always ends where the
     screen ends.

     The `-webkit-fill-available` fallback is deliberately declared FIRST so
     that `100svh` wins the cascade on browsers that support it. Declaring it
     last made it override the svh rule, and it resolves to the large
     viewport on iOS Safari - the exact problem we are fixing. */
  /* mobile viewport bug fix (legacy WebKit) */
  min-height: -webkit-fill-available;
  height: 100svh;
  min-height: 100svh;
  position: fixed;
  width: 100%;
  left: 0;
  top: 0;
}

#mainContent {
  background: #a2a2a2;
}

/* The canvas grey, extended under the sheet.

   The Konva canvas is TRANSPARENT wherever no grid is drawn - the grey is
   this element's own background showing through it. That background stops at
   #mainContent's bottom edge, which is the sheet's top edge, so the band the
   canvas overflows into (CanvasGrid's extend-behind-sheet, 40px) had
   nothing behind it.

   The sheet's rounded top corners are a CUTOUT: the card's white is clipped
   by its own border-radius, so the arc shows whatever is behind it. With no
   grey back there the cutout resolved to the page's white and the corner
   read as a white notch.

   The grey is a ::before rather than a background because a background would
   also cover the sheet's rounded corners. It sits at z-index -1 so it
   paints BEHIND the Konva canvas: the canvas overflows this element by the
   same 40px, and its grid art must stay on top of the grey. Painting the
   grey over the canvas instead (z-index 0) buried the grid under the sheet.

   Positioned rather than sized into the flow, so it cannot affect the
   layout or the collapse animation. */
#mainContent::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 40px;
  background: #a2a2a2;
  z-index: -1;
  pointer-events: none;
}

/* The bottom section's surface.

   The sheet's white card carries the rounded top corners, and the canvas
   shows through the cutouts as the design shows. Everything below the
   sheet is solid white - the sheet's own surface - and it runs to the
   bottom of the page.

   That lower white is what fixes the grey band under "Add to Cart" on iOS
   Safari: Safari lays the page out taller than the visible area and floats
   its bottom bar over the overflow, so the region behind the bar has to be
   the sheet's white, not the canvas grey. */
#studioContainer > div:last-child {
  position: relative;
}

/* The surface's TOP band is transparent, so the sheet's rounded corners -
   a cutout, not a border - reveal the canvas behind them.

   The band is as tall as the corner radius, plus a little so the arc's
   antialiased edge cannot leave a white hairline. Below it the surface is
   solid white, so the sheet itself is unaffected; only the corner cutouts
   show through.

   This is a GRADIENT rather than a solid colour because the surface's
   white has to start at an exact offset, and the same white continues
   below it as the sheet's own surface. */
.mobile-sheet-surface--open {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0,
    rgba(255, 255, 255, 0) calc(var(--sheet-corner-radius) + 4px),
    #FFFFFF calc(var(--sheet-corner-radius) + 4px),
    #FFFFFF 100%
  );
}

.mobile-sheet-surface--collapsed {
  background: transparent;
}

/* The white that runs past the bottom of the sheet.

   The sheet's wrapper is only as tall as the sheet itself (288px), so it
   stops flush with the footer's bottom edge. On iOS Safari the page is laid
   out taller than the visible area and the browser floats its bottom bar
   over that overflow - and #mainContent, which is flex-1, stretches down
   into it, so the canvas grey (#a2a2a2) shows through the bar as a grey
   band under "Add to Cart".

   This ::after extends a white backdrop BELOW the wrapper, behind the sheet
   (z-index 0) so it never covers the sheet's own surface or its rounded top
   corners. It is positioned rather than sized into the flow, so it cannot
   affect the layout or the collapse animation.

   100vh reaches past the bottom of the page on every device, which is what
   is needed here: the overflow behind Safari's bar is off-screen anyway. */
.mobile-sheet-surface::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 100vh;
  background: #FFFFFF;
  z-index: 0;
  pointer-events: none;
}

/* The root white that fills the region behind iOS Safari's bottom bar lives
   in assets/style.scss, not here. A scoped block is injected by this
   component's JavaScript at runtime, so on a cold load the rule can arrive
   after Safari has already composited the bar over the canvas grey. That
   file is linked in the document head and applies from the first paint. */
</style>
